/**
 * Os conectores da etapa 3 do onboarding — e QUAL modal cada um abre.
 *
 * ★ **Por que este arquivo existe.** Até 19/09/2026 o botão "Conectar" da
 * etapa 3 NAVEGAVA para a tela do módulo (`crm.calendar` / `crm.atendimento`),
 * tirando do chat quem estava no primeiro contato com a plataforma. O pedido do
 * owner foi trazer a conexão para dentro da conversa — sem tocar nos modais que
 * já existem naquelas telas, que continuam sendo o caminho de lá.
 *
 * Este mapa é o ÚNICO ponto que decide "este conector tem um modal aqui?", e
 * ele é lido por DOIS lados que não podem se importar:
 *
 * - `ui-registry/cartoes/cartao-onboarding.vue`, que precisa saber se emite
 *   `respond` (tem modal) ou se cai na navegação de sempre (não tem);
 * - `views/chat/view-chat.vue`, que precisa saber QUAL chave do `GlobalBus`
 *   abrir quando o `respond` chega.
 *
 * ⚠️ **O `rota` do catálogo do backend continua vivo, e é o FALLBACK.** Um
 * conector novo publicado por `core/onboarding/conectores.py` entra na lista
 * sem modal aqui e o botão dele segue navegando — nada regride. É por isso que
 * o mapa é uma consulta (`modalDoConector`) e não uma exigência.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { MODAL } from '@constants/Text'

/**
 * Os ids dos conectores — espelho MANUAL de `core/onboarding/conectores.py`
 * (`CONECTOR_EMAIL_AGENDA`, `CONECTOR_WHATSAPP`). Viajam em
 * `props.conectores[].id` e são a chave que o front usa; nunca o rótulo, que é
 * copy e muda sem aviso.
 */
export const CONECTOR_EMAIL_AGENDA = 'email_agenda'
export const CONECTOR_WHATSAPP = 'whatsapp'

/**
 * O vocabulário FECHADO do estado de um conector — espelho MANUAL de
 * `core/onboarding/conectores.py` (`ESTADO_CONECTADO`, `ESTADO_DESCONECTADO`,
 * `ESTADO_DESCONHECIDO`) e do enum de `ui-registry/plataforma.js`.
 *
 * ⚠️ **`desconhecido` é um TERCEIRO estado, não um sinônimo de desconectado.**
 * Ele é o da leitura que FALHOU, e o cartão o desenha sem o marcador e sem o
 * convite — `conectores.py`: *"dizer 'você não tem WhatsApp' porque uma query
 * falhou é afirmar sobre o cadastro de alguém um fato que não se apurou"*.
 *
 * ★ Mora AQUI porque este arquivo já é o espelho declarado daquele módulo do
 * backend (os ids e os provedores logo acima). ⚠️ `onboarding/decoradores.js`
 * tem as duas primeiras como constantes PRIVADAS — não dá para consolidar nesta
 * passagem (o cartão está fora do escopo); quem mexer lá importa daqui.
 */
export const ESTADO_CONECTADO = 'conectado'
export const ESTADO_DESCONECTADO = 'desconectado'
export const ESTADO_DESCONHECIDO = 'desconhecido'

/**
 * O canal do `GlobalBus` que pede a RECONFERÊNCIA do estado dos conectores.
 *
 * ★ **Módulo-local de propósito**, como o `RETORNO_OAUTH_KEY` logo abaixo: ele
 * liga DUAS peças desta superfície (o modal de e-mail/agenda, que revoga sem
 * sair da página, e o mixin da view, que relê) — pôr em `@constants/Text` o
 * anunciaria ao app inteiro para um fato que só o chat consome.
 *
 * ⚠️ **Não é o único gatilho.** A volta do OAuth recarrega a página, e a
 * releitura dali sai do `watch` do mixin; o WhatsApp já tem canal próprio
 * (`ACTION.ATENDIMENTO_CONEXAO_CONECTADA`, emitido pelo modal do QR e pelo de
 * Configurações). Este cobre a REVOGAÇÃO, que é a única transição que acontece
 * dentro da página sem nenhum dos dois.
 */
export const CANAL_RECONFERIR_CONECTORES =
  'ia_conectores_do_onboarding_reconferir'

/**
 * O azul da marca do Google.
 *
 * ★ **Divergência REGISTRADA** em `design-system/guidelines.md` → "Approved
 * divergences — `aawz-intelligence-v2` (cor de MARCA de conector de terceiro)".
 * O único token aplicável, `--primary`, é white-label: num escritório roxo o
 * glifo do Google sairia roxo, e o que identifica um conector de terceiro é
 * exatamente a cor dele.
 *
 * ⚠️ Mora AQUI, e não no decorador do cartão, porque agora são DOIS
 * consumidores (a linha da etapa 3 e o seletor de provedor do modal). Duas
 * cópias do mesmo hex divergiriam na primeira revisão — e o gate de cores
 * hardcoded acusaria as duas como achados independentes.
 */
export const AZUL_DO_GOOGLE = '#4285F4'

/**
 * Os provedores de e-mail/agenda oferecidos pelo modal, na ordem em que
 * aparecem no seletor.
 *
 * ⚠️ Os `id` são os que `integracao_oauth_token.provedor` de fato guarda
 * (`PROVEDORES_DE_EMAIL_AGENDA` em `core/onboarding/conectores.py`) — não há um
 * terceiro. O `icone` existe em `src/components/icons/svg/` (nome inexistente
 * renderiza VAZIO, sem erro, e some da tela calado).
 *
 * `corDoIcone` vazio = o glifo herda a cor do texto ao redor: o SVG da
 * Microsoft já é o quadriculado colorido da marca, enquanto o do Google é
 * monocromático e precisa do azul.
 */
export const PROVEDOR_GOOGLE = 'google'
export const PROVEDOR_MICROSOFT = 'microsoft'

export const PROVEDORES_DE_EMAIL_AGENDA = [
  {
    id: PROVEDOR_GOOGLE,
    nome: 'Google',
    icone: 'google',
    corDoIcone: AZUL_DO_GOOGLE,
  },
  {
    id: PROVEDOR_MICROSOFT,
    nome: 'Microsoft',
    icone: 'microsoft',
    corDoIcone: '',
  },
]

/**
 * A marca em `localStorage` que faz o modal de e-mail/agenda REABRIR quando o
 * provedor devolve a pessoa à plataforma.
 *
 * ⚠️ **O OAuth SAI da página, e não há como não sair.** O consentimento é do
 * Google/Microsoft, numa origem deles, e o fluxo implementado no backend é
 * redirect completo (`apiCrmGoogleIntegration.getAuthUrl` + `window.location`)
 * — não um popup com `postMessage`. Então o modal não "espera" o retorno: ele
 * deixa esta marca antes de sair e se reabre no `mounted()` da volta (o
 * `returnTo` é a URL corrente, logo a pessoa cai na MESMA conversa).
 *
 * ⚠️ É uma chave PRÓPRIA, e não o `AUTH_STARTED_KEY` do CRM: aquela é lida
 * pelo `crm-providers-integration.vue`, e reusá-la faria os dois modais
 * abrirem juntos em quem estivesse numa tela de CRM.
 */
export const RETORNO_OAUTH_KEY = 'aawz_ia_conectar_email_agenda'

const MODAL_POR_CONECTOR = {}
MODAL_POR_CONECTOR[CONECTOR_EMAIL_AGENDA] = MODAL.IA_CONECTAR_EMAIL_AGENDA
MODAL_POR_CONECTOR[CONECTOR_WHATSAPP] = MODAL.IA_CONECTAR_WHATSAPP

/**
 * A chave de `GlobalBus` que conecta este conector DENTRO do chat — ou `null`
 * quando ele só tem o caminho de navegação.
 *
 * @param {String} id O `id` do conector, como veio do backend.
 * @returns {?String}
 * @example modalDoConector('whatsapp') // → 'modal_ia_conectar_whatsapp'
 * @example modalDoConector('pipedrive') // → null (cai na rota do catálogo)
 */
export function modalDoConector(id) {
  if (!id) return null
  return MODAL_POR_CONECTOR[id] || null
}
